/**
 * @module  public/js/services/messagingService.js
 * @class   MessagingService
 * @summary Minimal client for sending SMS and managing optimistic message state.
 *
 * @description
 * Wraps the Gen-2 `messagingSend` HTTPS endpoint with Firebase-authenticated
 * requests using the existing `ApiClient`. The service keeps a lightweight
 * in-memory cache per thread, inserts optimistic outbound messages immediately
 * and reconciles them once Firestore updates arrive via realtime listeners.
 * Phone numbers are normalised to E.164 before network calls to mirror backend
 * validation.
 */

import {
  onSnapshot,
  orderBy,
  query,
} from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { colWithTenant } from '../utils/firestoreHelpers.js';
import TenantResolver from './tenantResolver.js';

/**
 * Normalise arbitrary phone number input to E.164.
 *
 * @param {string} value - Raw phone input from UI controls.
 * @returns {string} E.164 formatted string.
 * @throws {Error} when the value cannot be represented as E.164.
 */
function normaliseE164(value) {
  const raw = String(value ?? '').trim();
  if (!raw) throw new Error('Phone number is required');
  if (/^\+[1-9]\d{9,14}$/.test(raw)) {
    return raw;
  }
  const digits = raw.replace(/\D+/g, '');
  if (digits.length < 10) {
    throw new Error('Phone number must contain at least 10 digits');
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+${digits}`;
  }
  if (digits.length === 10) {
    return `+1${digits}`;
  }
  return `+${digits}`;
}

/**
 * Generate a deterministic thread identifier mirroring the backend logic.
 *
 * @param {string} ownerUid - Authenticated user identifier.
 * @param {string} counterpartyE164 - Counterparty phone number.
 * @returns {string} Thread identifier used in Firestore.
 */
function buildThreadId(ownerUid, counterpartyE164) {
  const digits = String(counterpartyE164).replace(/^\+/, '');
  return `${ownerUid}__${digits}`;
}

/**
 * MessagingService orchestrates outbound sends and message listeners.
 */
export default class MessagingService {
  /** @type {import('../core/apiclient.js').default} */ #apiClient;
  /** @type {import('../services/authservice.js').default} */ #auth;
  /** @type {TenantResolver} */ #tenantResolver;
  /** @type {Map<string, Array<Record<string, any>>>} */ #messages = new Map();
  /** @type {Map<string, Set<Function>>} */ #listeners = new Map();
  /** @type {Map<string, Function>} */ #unsubs = new Map();
  /** @type {Map<string, Map<string, any>>} */ #optimistic = new Map();

  /**
   * @param {object} deps
   * @param {import('../core/apiclient.js').default} deps.apiClient - Shared HTTP client.
   * @param {import('../services/authservice.js').default} deps.authService - Auth helper.
   * @param {TenantResolver} [deps.tenantResolver] - Optional resolver override.
   */
  constructor({ apiClient, authService, tenantResolver }) {
    if (!apiClient || !authService) {
      throw new Error('MessagingService requires ApiClient and AuthService');
    }
    this.#apiClient = apiClient;
    this.#auth = authService;
    this.#tenantResolver = tenantResolver || new TenantResolver(authService);
  }

  /**
   * Subscribe to realtime updates for the given thread.
   *
   * @param {string} threadId - Firestore thread identifier.
   * @param {(messages: Array<Record<string, any>>) => void} callback - Listener.
   * @returns {() => void} Unsubscribe function.
   */
  onThread(threadId, callback) {
    if (!this.#listeners.has(threadId)) {
      this.#listeners.set(threadId, new Set());
    }
    const set = this.#listeners.get(threadId);
    set.add(callback);
    if (!this.#messages.has(threadId)) {
      this.#messages.set(threadId, []);
    }
    callback(this.#messages.get(threadId));
    return () => {
      set.delete(callback);
      if (set.size === 0) {
        this.#listeners.delete(threadId);
      }
    };
  }

  /**
   * Send an SMS message via the Gen-2 endpoint and insert an optimistic entry.
   *
   * @param {{ to: string, body: string, mediaUrls?: string[], taskId?: string }} payload - Outbound message.
   * @returns {Promise<{ sid: string, threadId: string }>} Twilio SID + thread metadata.
   */
  async send(payload) {
    if (!payload || typeof payload !== 'object') {
      throw new Error('MessagingService.send requires a payload');
    }
    const to = normaliseE164(payload.to);
    const body = String(payload.body ?? '').trim();
    if (!body) throw new Error('Message body is required');
    const media = Array.isArray(payload.mediaUrls)
      ? payload.mediaUrls.map((url) => String(url || '').trim()).filter(Boolean)
      : [];

    const user = await this.#auth.requireAuth();
    const tenantId = await this.#tenantResolver.resolveTenantId();
    if (!tenantId) {
      throw new Error('Tenant could not be resolved');
    }

    const threadId = buildThreadId(user.uid, to);
    this.#ensureListener(tenantId, threadId);

    const optimisticId = `local-${Date.now().toString(36)}`;
    const optimisticMessage = {
      id: optimisticId,
      direction: 'outbound',
      to,
      from: null,
      body,
      media,
      status: 'queued',
      createdAt: new Date().toISOString(),
      optimistic: true,
    };
    this.#insertOptimistic(threadId, optimisticId, optimisticMessage);

    let response;
    const endpoints = this.#apiClient.endpoints;
    const url =
      (endpoints?.messagingSend
        ? endpoints.messagingSend({})
        : endpoints?.roots?.messagingSend) || '/messagingSend';

    try {
      response = await this.#apiClient.fetchJson(url, {
        auth: true,
        body: {
          tenantId,
          to,
          body,
          mediaUrls: media,
          taskId: payload.taskId ? String(payload.taskId) : undefined,
        },
      });
    } catch (err) {
      this.#removeOptimistic(threadId, optimisticId);
      throw err;
    }

    if (!response || !response.sid) {
      this.#removeOptimistic(threadId, optimisticId);
      throw new Error('Unexpected messagingSend response');
    }

    this.#updateOptimistic(threadId, optimisticId, {
      twilioSid: response.sid,
      optimistic: true,
    });

    return { sid: response.sid, threadId: response.threadId || threadId };
  }

  /**
   * Ensure a Firestore listener exists for the thread so optimistic messages reconcile.
   *
   * @param {string} tenantId - Tenant identifier.
   * @param {string} threadId - Thread identifier.
   */
  #ensureListener(tenantId, threadId) {
    if (this.#unsubs.has(threadId)) return;
    const col = colWithTenant(tenantId, `threads/${threadId}/messages`);
    const q = query(col, orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      this.#mergeMessages(threadId, docs);
    });
    this.#unsubs.set(threadId, unsub);
  }

  /**
   * Insert an optimistic message into the cache and notify listeners.
   *
   * @param {string} threadId - Thread identifier.
   * @param {string} optimisticId - Local optimistic identifier.
   * @param {Record<string, any>} message - Message payload.
   */
  #insertOptimistic(threadId, optimisticId, message) {
    if (!this.#optimistic.has(threadId)) {
      this.#optimistic.set(threadId, new Map());
    }
    const pending = this.#optimistic.get(threadId);
    pending.set(optimisticId, { ...message });
    const current = this.#messages.get(threadId) || [];
    this.#messages.set(threadId, [
      ...current,
      { ...message, id: optimisticId },
    ]);
    this.#emit(threadId);
  }

  /**
   * Remove an optimistic message from local caches.
   *
   * @param {string} threadId - Thread identifier.
   * @param {string} optimisticId - Local optimistic identifier.
   */
  #removeOptimistic(threadId, optimisticId) {
    const pending = this.#optimistic.get(threadId);
    if (pending) pending.delete(optimisticId);
    const next = (this.#messages.get(threadId) || []).filter(
      (msg) => msg.id !== optimisticId
    );
    this.#messages.set(threadId, next);
    this.#emit(threadId);
  }

  /**
   * Update an optimistic message with additional data (e.g. SID).
   *
   * @param {string} threadId - Thread identifier.
   * @param {string} optimisticId - Local optimistic identifier.
   * @param {Record<string, any>} patch - Partial update payload.
   */
  #updateOptimistic(threadId, optimisticId, patch) {
    const pending = this.#optimistic.get(threadId);
    if (!pending) return;
    const existing = pending.get(optimisticId);
    if (!existing) return;
    pending.set(optimisticId, { ...existing, ...patch });
    const list = this.#messages.get(threadId) || [];
    const updated = list.map((msg) =>
      msg.id === optimisticId ? { ...msg, ...patch } : msg
    );
    this.#messages.set(threadId, updated);
    this.#emit(threadId);
  }

  /**
   * Merge Firestore snapshots with optimistic entries and notify subscribers.
   *
   * @param {string} threadId - Thread identifier.
   * @param {Array<Record<string, any>>} docs - Snapshot payload.
   */
  #mergeMessages(threadId, docs) {
    const pending = this.#optimistic.get(threadId) || new Map();
    const materialised = docs.map((doc) => ({ ...doc, optimistic: false }));
    const remaining = Array.from(pending.values()).filter(
      (opt) =>
        !materialised.some(
          (doc) => doc.twilioSid && doc.twilioSid === opt.twilioSid
        )
    );
    if (remaining.length === 0) {
      this.#optimistic.delete(threadId);
    } else {
      this.#optimistic.set(
        threadId,
        new Map(remaining.map((msg) => [msg.id || msg.twilioSid, msg]))
      );
    }
    this.#messages.set(threadId, [...materialised, ...remaining]);
    this.#emit(threadId);
  }

  /**
   * Notify listeners of thread updates.
   *
   * @param {string} threadId - Thread identifier.
   */
  #emit(threadId) {
    const listeners = this.#listeners.get(threadId);
    if (!listeners) return;
    const data = this.#messages.get(threadId) || [];
    listeners.forEach((cb) => {
      try {
        cb(data);
      } catch (err) {
        console.error('[MessagingService] listener error', err); // eslint-disable-line no-console
      }
    });
  }
}
